import axios from 'axios';
import { PeppolResult } from '../types';
import { getGeneralError, getPeppolError, SupportedLanguage } from '../utils/errorTranslations';
import { Analytics, ErrorTracker, PerformanceTracker } from '../utils/analytics';

export class PeppolVerificationService {
  private static readonly PEPPOL_LOOKUP_URL = '/api/peppol/';
  private static useRealAPI = true; // Toggle for real vs simulated API calls

  static setUseRealAPI(useReal: boolean) {
    this.useRealAPI = useReal;
  }

  static async verifyPeppolId(participantId: string, language: SupportedLanguage = 'en'): Promise<PeppolResult> {
    try {
      Analytics.verificationStarted('peppol', 'single');
      PerformanceTracker.startTimer(`peppol_verification_${participantId}`);
      
      // Choose between real API call or simulation
      const result = this.useRealAPI 
        ? await this.callPeppolAPI(participantId, language)
        : await this.simulatePeppolLookup(participantId, language);

      const responseTime = PerformanceTracker.endTimer(`peppol_verification_${participantId}`, 'api_response_time', { service: 'peppol' });
      console.log('Response time:', responseTime);
      if (result.isValid) {
        Analytics.verificationSuccess('peppol', 'single');
      } else {
        Analytics.verificationError('peppol', 'single', result.error || 'validation_failed');
      }

      return {
        participantId: result.participantId || participantId, // Use from API response
        fullParticipantId: result.fullParticipantId,
        isValid: result.isValid,
        participantName: result.participantName,
        scheme: result.scheme,
        timestamp: new Date().toISOString(),
        error: result.error,
        allParticipantIds: result.allParticipantIds,
        allEntityNames: result.allEntityNames,
        matchCount: result.matchCount,
        allSupportedDocumentTypes: result.allSupportedDocumentTypes,
        supportsBilling: result.supportsBilling
      };
    } catch (error) {
      const responseTime = PerformanceTracker.endTimer(`peppol_verification_${participantId}`);
      console.log('Response time:', responseTime);
      Analytics.verificationError('peppol', 'single', 'api_error');
      ErrorTracker.trackApiError('peppol', error, 'verifyPeppolId');
      
      return {
        participantId,
        isValid: false,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : getGeneralError('unknownError', language)
      };
    }
  }

  private static async callPeppolAPI(
    participantId: string,
    language: SupportedLanguage = 'en'
  ): Promise<{
    isValid: boolean;
    participantName?: string;
    scheme?: string;
    participantId?: string;
    fullParticipantId?: string;
    error?: string;
    allParticipantIds?: string;
    allEntityNames?: string;
    matchCount?: number;
    allSupportedDocumentTypes?: string;
    supportsBilling?: boolean;
  }> {
    const maxRetries = 7;
    const retryDelay = 2000; // 2 seconds
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Remove first 2 letters if participantId starts with 2 letters
        let queryParam = participantId;
        if (participantId.length >= 2 && /^[A-Za-z]{2}/.test(participantId)) {
          queryParam = participantId.substring(2);
        }
        let response = await axios.get(`${this.PEPPOL_LOOKUP_URL}search/1.0/json?q=${queryParam}`);
        
        // Check if matches array exists but is empty (invalid participant)
        if (response.data && response.data.matches && response.data.matches.length === 0) {
          return {
            isValid: false
          };
        }

        if (response.data && response.data.matches !== undefined) {
          // Process matches if they exist
          const matches = response.data.matches;
          const matchCount = matches.length;
          
          // Process the first match for primary result
          const firstMatch = matches[0];
          const firstEntity = firstMatch.entities && firstMatch.entities[0];
          
          // Extract participant name from the nested structure
          let participantName = 'Unknown';
          if (firstEntity?.name) {
            console.log('Entity name structure:', firstEntity.name);
            if (Array.isArray(firstEntity.name)) {
              // If name is an array, get the first one
              participantName = firstEntity.name[0]?.name || 'Unknown';
              console.log('Extracted name from array:', participantName);
            } else if (typeof firstEntity.name === 'object' && firstEntity.name.name) {
              // If name is an object with name property
              participantName = firstEntity.name.name;
              console.log('Extracted name from object:', participantName);
            } else if (typeof firstEntity.name === 'string') {
              // If name is already a string
              participantName = firstEntity.name;
              console.log('Name is already string:', participantName);
            }
          }
          
          // Ensure participantName is always a string
          if (typeof participantName !== 'string') {
            console.warn('Participant name is not a string:', participantName);
            participantName = String(participantName) || 'Unknown';
          }
          
          // Extract scheme and participant ID from the first match
          const scheme = firstMatch.participantID?.scheme || this.extractSchemeFromParticipantId(participantId);
          const responseParticipantId = firstMatch.participantID?.value || participantId;
          
          // Construct full participant ID by combining scheme and participant ID
          let fullParticipantId = responseParticipantId;
          if (scheme && !responseParticipantId.includes('::')) {
            // If we have a scheme but the participant ID doesn't include it, combine them
            fullParticipantId = `${scheme}::${responseParticipantId}`;
          }
          
          // Process all matches to collect all participant IDs, entity names, and supported document types
          const allParticipantIds: string[] = [];
          const allEntityNames: string[] = [];
          const allSupportedDocumentTypes: string[] = [];
          let supportsBilling = false;
          
          matches.forEach((match: any) => {
            // Extract participant ID
            const matchScheme = match.participantID?.scheme || this.extractSchemeFromParticipantId(participantId);
            const matchParticipantId = match.participantID?.value || participantId;
            let matchFullParticipantId = matchParticipantId;
            if (matchScheme && !matchParticipantId.includes('::')) {
              matchFullParticipantId = `${matchScheme}::${matchParticipantId}`;
            }
            allParticipantIds.push(matchFullParticipantId);
            
            // Extract entity name
            const entity = match.entities && match.entities[0];
            let entityName = 'Unknown';
            if (entity?.name) {
              if (Array.isArray(entity.name)) {
                entityName = entity.name[0]?.name || 'Unknown';
              } else if (typeof entity.name === 'object' && entity.name.name) {
                entityName = entity.name.name;
              } else if (typeof entity.name === 'string') {
                entityName = entity.name;
              }
            }
            if (typeof entityName !== 'string') {
              entityName = String(entityName) || 'Unknown';
            }
            allEntityNames.push(entityName);
            
            // Extract supported document types from docTypes array
            const documentTypes: string[] = [];
            let hasInvoiceBilling = false;
            let hasCreditNoteBilling = false;
            
            if (match.docTypes && Array.isArray(match.docTypes)) {
              match.docTypes.forEach((docType: any) => {
                if (docType && docType.value && typeof docType.value === 'string') {
                  documentTypes.push(docType.value);
                  // Check for specific billing document types
                  if (docType.value.includes('urn:fdc:peppol.eu:2017:poacc:billing')) {
                    if (docType.value.includes('Invoice')) {
                      hasInvoiceBilling = true;
                    }
                    if (docType.value.includes('CreditNote')) {
                      hasCreditNoteBilling = true;
                    }
                  }
                }
              });
            }
            
            // Only mark as supporting billing if BOTH invoice and credit note billing types are present
            if (hasInvoiceBilling && hasCreditNoteBilling) {
              supportsBilling = true;
            }
            
            allSupportedDocumentTypes.push(documentTypes.join(','));
          });
          
          return {
            isValid: true,
            participantName: participantName,
            scheme: scheme,
            participantId: responseParticipantId,
            fullParticipantId: fullParticipantId,
            allParticipantIds: allParticipantIds.join('|'),
            allEntityNames: allEntityNames.join('|'),
            matchCount: matchCount,
            allSupportedDocumentTypes: allSupportedDocumentTypes.join('|'),
            supportsBilling: supportsBilling
          };
        } else {
          // No matches field in response - this is an error, not invalid
          throw new Error('No matches found in response - retrying');
        }
      } catch (error) {
        console.error(`Peppol API Error (attempt ${attempt}/${maxRetries}):`, error);
        
        // If this is the last attempt, return the error
        if (attempt === maxRetries) {
          return {
            isValid: false,
            error: getPeppolError('serviceUnavailable', language)
          };
        }
        
        // Wait before retrying (except on the last attempt)
        console.log(`Retrying in ${attempt * retryDelay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, attempt * retryDelay));
      }
    }
    
    // This should never be reached, but just in case
    return {
      isValid: false,
      error: getPeppolError('serviceUnavailable', language)
    };
  }

  private static async simulatePeppolLookup(
    participantId: string,
    language: SupportedLanguage = 'en'
  ): Promise<{
    isValid: boolean;
    participantName?: string;
    scheme?: string;
    participantId?: string;
    fullParticipantId?: string;
    error?: string;
    allParticipantIds?: string;
    allEntityNames?: string;
    matchCount?: number;
    allSupportedDocumentTypes?: string;
    supportsBilling?: boolean;
  }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1500));

    // Validate participant ID format
    if (!this.validatePeppolIdFormat(participantId)) {
      return {
        isValid: false,
        error: getPeppolError('invalidFormat', language)
      };
    }

    // Simulate different responses based on participant ID patterns
    const isValid = this.simulatePeppolValidation(participantId);
    
    if (!isValid) {
      return {
        isValid: false
      };
    }

    // Simulate successful verification
    const scheme = this.extractScheme(participantId);
    
    // Construct full participant ID by combining scheme and participant ID
    let fullParticipantId = participantId;
    if (scheme && !participantId.includes('::')) {
      // If we have a scheme but the participant ID doesn't include it, combine them
      fullParticipantId = `${scheme}::${participantId}`;
    }
    
    // Simulate multiple matches (30% chance of having multiple matches)
    const hasMultipleMatches = Math.random() < 0.3;
    let allParticipantIds = fullParticipantId;
    let allEntityNames = this.generateParticipantName(participantId);
    let allSupportedDocumentTypes = this.generateSupportedDocumentTypes();
    let supportsBilling = allSupportedDocumentTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1') && 
                         allSupportedDocumentTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1');
    let matchCount = 1;
    
    if (hasMultipleMatches) {
      // Generate 2-4 additional matches
      const additionalMatches = Math.floor(Math.random() * 3) + 2; // 2-4 matches
      const additionalIds: string[] = [fullParticipantId];
      const additionalNames: string[] = [this.generateParticipantName(participantId)];
      const additionalDocTypes: string[] = [allSupportedDocumentTypes];
      
      for (let i = 1; i < additionalMatches; i++) {
        // Generate variations of the participant ID
        const baseId = participantId.replace(/\d/g, (match, offset) => {
          // Change some digits to create variations
          return Math.random() < 0.3 ? String(Math.floor(Math.random() * 10)) : match;
        });
        
        const additionalScheme = this.extractScheme(baseId);
        let additionalFullId = baseId;
        if (additionalScheme && !baseId.includes('::')) {
          additionalFullId = `${additionalScheme}::${baseId}`;
        }
        
        additionalIds.push(additionalFullId);
        additionalNames.push(this.generateParticipantName(baseId));
        additionalDocTypes.push(this.generateSupportedDocumentTypes());
      }
      
      allParticipantIds = additionalIds.join(' | ');
      allEntityNames = additionalNames.join(' | ');
      allSupportedDocumentTypes = additionalDocTypes.join(' | ');
      supportsBilling = additionalDocTypes.some(docTypes => 
        docTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1') && 
        docTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1')
      );
      matchCount = additionalMatches;
    }
    
    return {
      isValid: true,
      participantName: this.generateParticipantName(participantId),
      scheme: scheme,
      participantId: participantId,
      fullParticipantId: fullParticipantId,
      allParticipantIds: allParticipantIds,
      allEntityNames: allEntityNames,
      matchCount: matchCount,
      allSupportedDocumentTypes: allSupportedDocumentTypes,
      supportsBilling: supportsBilling
    };
  }

  private static validatePeppolIdFormat(participantId: string): boolean {
    // Peppol participant IDs typically follow patterns like:
    // - GLN: 12-15 digits
    // - DUNS: 9 digits
    // - VAT: Country code + VAT number
    // - Custom schemes: Various formats
    // - Simple numeric IDs: 8-15 digits (common in UPIS)
    
    const trimmedId = participantId.trim();
    
    // Basic validation - should not be empty and should contain valid characters
    if (!trimmedId || trimmedId.length < 3) {
      return false;
    }

    // Check for common patterns
    const patterns = [
      /^\d{9}$/, // DUNS
      /^\d{12,15}$/, // GLN
      /^\d{8,15}$/, // Simple numeric IDs (8-15 digits for UPIS)
      /^[A-Z]{2}\d{8,12}$/, // VAT-like
      /^[A-Z0-9]{3,20}$/, // Generic alphanumeric
      /^iso6523-actorid-upis::\d{4}:\d+$/, // Full UPIS format with country code (e.g., iso6523-actorid-upis::0088:9429037080266)
      /^iso6523-actorid-upis::\d+$/, // Full UPIS format without country code
      /^iso6523-actorid-gln::\d+$/, // Full GLN format
      /^iso6523-actorid-duns::\d+$/ // Full DUNS format
    ];

    return patterns.some(pattern => pattern.test(trimmedId));
  }

  private static simulatePeppolValidation(participantId: string): boolean {
    // Simulate validation logic - in reality this would be an API call
    const id = participantId.trim();
    
    // Simulate some IDs as valid and others as invalid
    const hash = this.simpleHash(id);
    return hash % 3 !== 0; // 2/3 chance of being valid
  }

  private static simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  private static generateParticipantName(participantId: string): string {
    const id = participantId.trim();
    
    if (id.match(/^\d{9}$/)) {
      return `Company DUNS ${id}`;
    } else if (id.match(/^\d{12,15}$/)) {
      return `Company GLN ${id}`;
    } else if (id.match(/^\d{8,15}$/)) {
      return `Company UPIS ${id}`;
    } else if (id.match(/^[A-Z]{2}\d/)) {
      return `Company VAT ${id}`;
    } else if (id.match(/^iso6523-actorid-upis::\d{4}:\d+$/)) {
      // Extract the numeric part after the country code
      const numericPart = id.split(':').pop();
      return `Company UPIS ${numericPart}`;
    } else if (id.match(/^iso6523-actorid-upis::\d+$/)) {
      // Extract the numeric part
      const numericPart = id.split('::').pop();
      return `Company UPIS ${numericPart}`;
    } else {
      return `Company ${id}`;
    }
  }

  private static generateSupportedDocumentTypes(): string {
    // Common PEPPOL document types in the actual API format
    const documentTypes = [
      'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:Order-2::Order##urn:fdc:peppol.eu:poacc:trns:order:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:OrderResponse-2::OrderResponse##urn:fdc:peppol.eu:poacc:trns:order_response:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:DespatchAdvice-2::DespatchAdvice##urn:fdc:peppol.eu:poacc:trns:despatch_advice:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:ReceiptAdvice-2::ReceiptAdvice##urn:fdc:peppol.eu:poacc:trns:receipt_advice:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:Catalogue-2::Catalogue##urn:fdc:peppol.eu:poacc:trns:catalogue:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:CatalogueResponse-2::CatalogueResponse##urn:fdc:peppol.eu:poacc:trns:catalogue_response:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:Quotation-2::Quotation##urn:fdc:peppol.eu:poacc:trns:quotation:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:QuotationResponse-2::QuotationResponse##urn:fdc:peppol.eu:poacc:trns:quotation_response:3::2.1',
      'urn:oasis:names:specification:ubl:schema:xsd:ApplicationResponse-2::ApplicationResponse##urn:fdc:peppol.eu:poacc:trns:invoice_response:3::2.1'
    ];
    
    // Randomly select 2-5 document types
    const numTypes = Math.floor(Math.random() * 4) + 2; // 2-5 types
    const selectedTypes = [];
    
    // Always include billing if it's selected
    const includeBilling = Math.random() < 0.7; // 70% chance
    if (includeBilling) {
      // Add both invoice and credit note billing types
      selectedTypes.push('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1');
      selectedTypes.push('urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1');
    }
    
    // Add other random types
    const availableTypes = documentTypes.filter(type => 
      !type.includes('urn:fdc:peppol.eu:2017:poacc:billing')
    );
    const shuffled = availableTypes.sort(() => 0.5 - Math.random());
    
    const remainingSlots = numTypes - (includeBilling ? 2 : 0);
    for (let i = 0; i < remainingSlots && i < shuffled.length; i++) {
      selectedTypes.push(shuffled[i]);
    }
    
    return selectedTypes.join(',');
  }

  private static extractScheme(participantId: string): string {
    const id = participantId.trim();
    
    if (id.match(/^\d{9}$/)) {
      return 'DUNS';
    } else if (id.match(/^\d{12,15}$/)) {
      return 'GLN';
    } else if (id.match(/^\d{8,15}$/)) {
      return 'UPIS';
    } else if (id.match(/^[A-Z]{2}\d/)) {
      return 'VAT';
    } else if (id.match(/^iso6523-actorid-upis::/)) {
      return 'UPIS';
    } else if (id.match(/^iso6523-actorid-gln::/)) {
      return 'GLN';
    } else if (id.match(/^iso6523-actorid-duns::/)) {
      return 'DUNS';
    } else {
      return 'CUSTOM';
    }
  }

  private static isGLN(participantId: string): boolean {
    const id = participantId.trim();
    return /^\d{12,15}$/.test(id);
  }

  private static isDUNS(participantId: string): boolean {
    const id = participantId.trim();
    return /^\d{9}$/.test(id);
  }

  private static hasScheme(participantId: string): boolean {
    const id = participantId.trim();
    // Check if the participant ID already contains a scheme (contains ::)
    return id.includes('::');
  }

  private static getPossibleSchemes(participantId: string): string[] {
    const id = participantId.trim();
    
    // Return schemes in order of likelihood based on the participant ID format
    if (this.isGLN(id)) {
      return ['iso6523-actorid-gln'];
    } else if (this.isDUNS(id)) {
      return ['iso6523-actorid-duns'];
    } else if (id.match(/^[A-Z]{2}\d/)) {
      // VAT-like format
      return ['iso6523-actorid-upis'];
    } else if (id.match(/^\d{13}$/)) {
      // 13-digit numbers are commonly GLN or UPIS
      return ['iso6523-actorid-gln', 'iso6523-actorid-upis'];
    } else if (id.match(/^\d{12}$/)) {
      // 12-digit numbers are commonly GLN
      return ['iso6523-actorid-gln'];
    } else if (id.match(/^\d{8,15}$/)) {
      // Other numeric IDs - try UPIS first as it's most common
      return ['iso6523-actorid-upis', 'iso6523-actorid-gln'];
    } else {
      // Try common schemes in order of preference
      return [
        'iso6523-actorid-upis',
        'iso6523-actorid-gln',
        'iso6523-actorid-duns'
      ];
    }
  }

  private static formatParticipantIdForScheme(participantId: string, scheme: string): string {
    const id = participantId.trim();
    
    // If the participant ID already has a scheme, return as-is
    if (id.includes('::')) {
      return id;
    }
    
    // For UPIS scheme, we need to add a country code prefix
    if (scheme === 'iso6523-actorid-upis') {
      // Try to extract country code from VAT-like format first
      if (id.match(/^[A-Z]{2}\d/)) {
        const countryCode = id.substring(0, 2);
        const numericPart = id.substring(2);
        // Convert country code to numeric format (e.g., DE -> 0276, NZ -> 0088)
        const countryCodeNumeric = this.getCountryCodeNumeric(countryCode);
        return `${scheme}::${countryCodeNumeric}:${numericPart}`;
      } else if (id.match(/^\d{8,15}$/)) {
        // For simple numeric IDs, use a default country code (0088 for New Zealand as example)
        // In a real implementation, you might want to ask the user for the country
        return `${scheme}::0088:${id}`;
      }
    }
    
    // For other schemes, just add the scheme prefix
    return `${scheme}::${id}`;
  }

  private static getCountryCodeNumeric(countryCode: string): string {
    // Common country code mappings (ISO 3166-1 alpha-2 to numeric)
    const countryMappings: { [key: string]: string } = {
      'DE': '0276', // Germany
      'FR': '0250', // France
      'IT': '0380', // Italy
      'ES': '0724', // Spain
      'NL': '0528', // Netherlands
      'BE': '0056', // Belgium
      'AT': '0040', // Austria
      'SE': '0752', // Sweden
      'NO': '0578', // Norway
      'DK': '0208', // Denmark
      'FI': '0246', // Finland
      'PL': '0616', // Poland
      'CZ': '0203', // Czech Republic
      'HU': '0348', // Hungary
      'SK': '0703', // Slovakia
      'SI': '0705', // Slovenia
      'HR': '0191', // Croatia
      'RO': '0642', // Romania
      'BG': '0100', // Bulgaria
      'GR': '0300', // Greece
      'PT': '0620', // Portugal
      'IE': '0372', // Ireland
      'LU': '0442', // Luxembourg
      'MT': '0470', // Malta
      'CY': '0196', // Cyprus
      'EE': '0233', // Estonia
      'LV': '0428', // Latvia
      'LT': '0440', // Lithuania
      'NZ': '0088', // New Zealand
      'AU': '0036', // Australia
      'US': '0840', // United States
      'CA': '0124', // Canada
      'GB': '0826', // United Kingdom
      'CH': '0756', // Switzerland
      'JP': '0392', // Japan
      'CN': '0156', // China
      'IN': '0356', // India
      'BR': '0076', // Brazil
      'MX': '0484', // Mexico
      'KR': '0410', // South Korea
      'SG': '0702', // Singapore
      'HK': '0344', // Hong Kong
      'MY': '0458', // Malaysia
      'TH': '0764', // Thailand
      'ID': '0360', // Indonesia
      'PH': '0608', // Philippines
      'VN': '0704', // Vietnam
      'ZA': '0710'  // South Africa
    };
    
    return countryMappings[countryCode.toUpperCase()] || '0088'; // Default to New Zealand if not found
  }

  private static extractSchemeFromParticipantId(participantId: string): string {
    const id = participantId.trim();
    
    if (id.includes('::')) {
      return id.split('::')[0];
    }
    
    // Fallback to format-based detection
    if (this.isGLN(id)) {
      return 'iso6523-actorid-gln';
    } else if (this.isDUNS(id)) {
      return 'iso6523-actorid-duns';
    } else if (id.match(/^\d{8,15}$/)) {
      return 'iso6523-actorid-upis';
    } else {
      return 'iso6523-actorid-upis';
    }
  }

  static async verifyBatch(participantIds: string[]): Promise<PeppolResult[]> {
    const results: PeppolResult[] = [];
    
    for (const participantId of participantIds) {
      const result = await this.verifyPeppolId(participantId);
      results.push(result);
    }
    
    return results;
  }
}
